/**
 * Skin Quiz - client-side logic
 * /assets/skin-quiz.js
 *
 * Step flow:
 *   landing -> name -> q1..q7 -> email -> results
 *
 * Skin type resolution:
 *   Q3 base answer (+1 vote) + Q4 sensitive check (Yes -> +1 Sensitive).
 *   Original Q4 (oily) and Q5 (dry) checks removed per request.
 *
 * Klaviyo:
 *   klaviyo.identify() fires after email step (email + name now available).
 *   klaviyo.track('Skin Quiz Completed') fires when results render.
 *   Both use klaviyo.push([...]) so calls queue safely before klaviyo.js loads.
 *   Marketing consent is captured via a checkbox on the email step and
 *   passed through to identify() as $consent so Klaviyo flow emails are
 *   actually allowed to send.
 *
 * Add to Cart:
 *   Per-product button + "Add all to cart" batch button.
 *   Uses /cart/add.js; dispatches cart:update for Horizon's cart drawer.
 */

(function () {
  'use strict';

  /** @typedef {{ id:string, variantId:string, title:string, url:string, image:string|null, price:string, tags:string[] }} QuizProduct */

  function initSkinQuiz(root) {

    /* ------------------------------------------------------------------
       Step registry
    ------------------------------------------------------------------ */
    var stepOrder = ['landing', 'name', 'q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'email', 'results'];
    var questionSteps = ['q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7'];
    var allStepEls = Array.prototype.slice.call(root.querySelectorAll('[data-quiz-step]'));
    var currentStepIndex = 0;

    var visitor = { name: '', email: '', consent: false };
    var answers  = {};
    var currentRoutine = [];

    function getStepEl(key) {
      return root.querySelector('[data-quiz-step="' + key + '"]');
    }

    function showStep(key) {
      allStepEls.forEach(function (el) {
        el.classList.toggle('is-active', el.getAttribute('data-quiz-step') === key);
      });
      currentStepIndex = stepOrder.indexOf(key);
      updateProgress();

      var activeEl = getStepEl(key);
      var heading  = activeEl && activeEl.querySelector('.skin-quiz__heading');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        heading.focus();
      }
    }

    function updateProgress() {
      var fill = root.querySelector('[data-quiz-progress-fill]');
      if (!fill) return;

      var key = stepOrder[currentStepIndex];
      var pct;

      if (key === 'landing' || key === 'name') {
        pct = 0;
      } else if (key === 'results') {
        pct = 100;
      } else if (key === 'email') {
        // email step sits after all questions -> treat as ~95%
        pct = 95;
      } else {
        var qi = questionSteps.indexOf(key);
        pct = ((qi + 1) / questionSteps.length) * 90; // 0-90 during questions
      }
      fill.style.width = pct + '%';
    }

    /* ------------------------------------------------------------------
       Landing step: "Start Quiz" button -> name step
    ------------------------------------------------------------------ */
    var landingBtn = root.querySelector('[data-quiz-to="name"]');
    if (landingBtn) {
      landingBtn.addEventListener('click', function () { showStep('name'); });
    }

    /* ------------------------------------------------------------------
       Name step
    ------------------------------------------------------------------ */
    var nameInput    = root.querySelector('[data-quiz-input="name"]');
    var nameError    = root.querySelector('[data-quiz-error="name"]');
    var nameContinue = root.querySelector('[data-quiz-name-continue]');
    var backToLanding = root.querySelector('[data-quiz-to="landing"]');

    if (backToLanding) {
      backToLanding.addEventListener('click', function () { showStep('landing'); });
    }

    if (nameContinue) {
      nameContinue.addEventListener('click', function () {
        var val = (nameInput && nameInput.value || '').trim();
        if (!val) {
          if (nameError) nameError.hidden = false;
          return;
        }
        if (nameError) nameError.hidden = true;
        visitor.name = val;

        // Personalise the email step heading with their name.
        var nameDisplay = root.querySelector('[data-quiz-name-display]');
        if (nameDisplay) nameDisplay.textContent = val;

        showStep('q1');
      });
    }

    // Allow Enter key on name input.
    if (nameInput) {
      nameInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && nameContinue) nameContinue.click();
      });
    }

    /* ------------------------------------------------------------------
       Question steps (q1..q7)
    ------------------------------------------------------------------ */
    questionSteps.forEach(function (stepKey) {
      var stepEl = getStepEl(stepKey);
      if (!stepEl) return;

      var optionBtns  = Array.prototype.slice.call(stepEl.querySelectorAll('[data-quiz-option]'));
      var continueBtn = stepEl.querySelector('[data-quiz-continue]');
      var backBtn     = stepEl.querySelector('[data-quiz-back]');

      optionBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          optionBtns.forEach(function (b) { b.classList.remove('is-selected'); });
          btn.classList.add('is-selected');

          var voteAttr = btn.getAttribute('data-vote') || '';
          var votes = voteAttr
            ? voteAttr.split(',').map(function (v) { return v.trim(); }).filter(Boolean)
            : [];

          answers[btn.getAttribute('data-question')] = {
            value:       btn.getAttribute('data-value'),
            skintypeTag: btn.getAttribute('data-skintype-tag') || '',
            concernTag:  btn.getAttribute('data-concern-tag')  || '',
            votes:       votes
          };

          if (continueBtn) continueBtn.disabled = false;
        });
      });

      if (continueBtn) {
        continueBtn.addEventListener('click', function () {
          var idx  = stepOrder.indexOf(stepKey);
          var next = stepOrder[idx + 1];
          showStep(next);
        });
      }

      if (backBtn) {
        backBtn.addEventListener('click', function () {
          var idx  = stepOrder.indexOf(stepKey);
          var prev = stepOrder[idx - 1];
          showStep(prev);
        });
      }
    });

    /* ------------------------------------------------------------------
       Email step (last before results)
    ------------------------------------------------------------------ */
    var emailInput    = root.querySelector('[data-quiz-input="email"]');
    var emailError    = root.querySelector('[data-quiz-error="email"]');
    var consentInput  = root.querySelector('[data-quiz-input="consent"]');
    var emailContinue = root.querySelector('[data-quiz-email-continue]');
    var emailBackBtn  = getStepEl('email') && getStepEl('email').querySelector('[data-quiz-back]');

    function isValidEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    }

    if (emailContinue) {
      emailContinue.addEventListener('click', function () {
        var val = (emailInput && emailInput.value || '').trim();
        if (!val || !isValidEmail(val)) {
          if (emailError) emailError.hidden = false;
          return;
        }
        if (emailError) emailError.hidden = true;
        visitor.email   = val;
        visitor.consent = consentInput ? consentInput.checked : true;

        // Identify in Klaviyo now that we have both name and email.
        identifyVisitor(visitor);

        renderResults();
        showStep('results');
      });
    }

    // Allow Enter key on email input.
    if (emailInput) {
      emailInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' && emailContinue) emailContinue.click();
      });
    }

    if (emailBackBtn) {
      emailBackBtn.addEventListener('click', function () {
        showStep('q7');
      });
    }

    /* ------------------------------------------------------------------
       Restart
    ------------------------------------------------------------------ */
    var restartBtn = root.querySelector('[data-quiz-restart]');
    if (restartBtn) {
      restartBtn.addEventListener('click', function () {
        answers = {};
        visitor = { name: '', email: '', consent: false };
        currentRoutine = [];

        allStepEls.forEach(function (stepEl) {
          stepEl.querySelectorAll('[data-quiz-option]').forEach(function (b) {
            b.classList.remove('is-selected');
          });
          var cont = stepEl.querySelector('[data-quiz-continue]');
          if (cont) cont.disabled = true;
        });

        if (nameInput)   nameInput.value  = '';
        if (emailInput)  emailInput.value = '';
        if (consentInput) consentInput.checked = true;

        showStep('landing');
      });
    }

    /* ------------------------------------------------------------------
       Skin type resolution
       Q3 base answer: +1 vote for its skin type.
       Q4 sensitive check: Yes -> +1 Sensitive.
       Highest total wins; ties keep Q3's base answer.
    ------------------------------------------------------------------ */
    function resolveSkinType() {
      var votes    = {};
      var baseType = answers.skintype_base && answers.skintype_base.skintypeTag;

      if (baseType) votes[baseType] = (votes[baseType] || 0) + 1;

      var sensitiveAns = answers.check_sensitive;
      if (sensitiveAns && sensitiveAns.votes && sensitiveAns.votes.length) {
        sensitiveAns.votes.forEach(function (type) {
          votes[type] = (votes[type] || 0) + 1;
        });
      }

      var winnerType  = baseType || '';
      var winnerVotes = baseType ? (votes[baseType] || 0) : 0;

      Object.keys(votes).forEach(function (type) {
        if (votes[type] > winnerVotes) {
          winnerVotes = votes[type];
          winnerType  = type;
        }
      });

      return winnerType;
    }

    var FALLBACK_CONCERN_TAG = 'Sensitive / Redness / Barrier Repair';

    function resolveConcernTag() {
      var ans = answers.concern;
      return (ans && ans.concernTag) || FALLBACK_CONCERN_TAG;
    }

    function resolveSkinTone() {
      var ans = answers.skintone;
      return (ans && ans.value) || '';
    }

    /* ------------------------------------------------------------------
       Routine profiles (Q7)
    ------------------------------------------------------------------ */
    var ROUTINE_PROFILES = {
      'Minimal (1-3 steps)': {
        categories:         ['Gel Cleanser', 'Serum', 'Moisturiser'],
        optionalCategories: []
      },
      'Medium (4-6 steps)': {
        categories:         ['Oil Cleanser', 'Gel Cleanser', 'Toner', 'Essence', 'Serum', 'Moisturiser'],
        optionalCategories: ['Oil Cleanser']
      },
      'I love a full routine!': {
        categories:         ['Oil Cleanser', 'Gel Cleanser', 'Toner', 'Essence', 'Serum', 'Moisturiser', 'Eye Cream', 'Face Mask'],
        optionalCategories: []
      }
    };
    var DEFAULT_ROUTINE_PROFILE = ROUTINE_PROFILES['I love a full routine!'];

    function resolveRoutineProfile() {
      var ans = answers.routine;
      return (ans && ROUTINE_PROFILES[ans.value]) || DEFAULT_ROUTINE_PROFILE;
    }

    /* ------------------------------------------------------------------
       Educational blurbs
    ------------------------------------------------------------------ */
    var AGE_BLURBS = {
      'Under 18': 'Your skin is still developing, and oiliness or breakouts are common. Stick to gentle cleansers, lightweight moisturizers, and sunscreen. Avoid strong actives like retinol unless advised by a professional.',
      '18–24': 'Your skin may be prone to oiliness, breakouts, or occasional dryness. It’s a great time to build good skincare habits with gentle cleansing, hydration, and daily sun protection.',
      '25–34': 'This is when your skin’s natural renewal may start slowing slightly. Focus on hydration, gentle exfoliation, and brightening ingredients like vitamin C or niacinamide to maintain glow and even skin tone.',
      '35–44': 'Collagen and elasticity naturally decline during these years. Hydrating serums, antioxidants, and peptides help maintain firmness and brightness. Consistent sun protection is essential.',
      '45–54': 'Your skin may become drier and lose elasticity as hormone levels change. Richer moisturizers, barrier repair ingredients, and firming serums support resilience. Brightening products also help maintain an even tone.',
      '55+': 'Skin can become thinner, drier, and more fragile. Deep hydration, barrier repair, and soothing ingredients become even more important. Focus on moisture-rich routines, gentle exfoliation, and sun protection.'
    };

    var SKIN_TYPE_BLURBS = {
      'Normal':      'Balanced skin feels comfortable — not too oily or too dry. A mild routine helps maintain this balance.',
      'Oily':        'Oily skin produces excess sebum, leading to shine and potential breakouts. Oil control and lightweight hydration help keep it balanced.',
      'Dry':         'Dry skin often feels tight, rough, or flaky. Rich hydration and barrier repair ingredients are key to comfort.',
      'Combination': 'Combination skin is oily in the T-zone but normal or dry elsewhere. Balance is essential to avoid over-drying or excess oil.',
      'Sensitive':   'Sensitive skin reacts easily to products, with redness, stinging, or irritation. Gentle, fragrance-free formulations are important.'
    };

    var CONCERN_BLURBS = {
      'Acne / breakouts':             'Acne can result from excess oil, clogged pores, and inflammation. Gentle cleansers and targeted treatments like BHA can help manage breakouts.',
      'Dryness / flakiness':          'Dry skin lacks moisture or oil, leading to rough texture. Rich creams and barrier repair ingredients restore comfort.',
      'Sensitivity / redness':        'Sensitive skin is easily irritated. Look for calming ingredients like centella asiatica and panthenol, and avoid harsh actives.',
      'Fine lines / wrinkles':        'Fine lines start appearing due to decreased collagen production. Peptides, retinol (if suitable), and hydration can help smooth their appearance.',
      'Dark spots / uneven skin tone':'Hyperpigmentation comes from sun exposure, acne scars, or hormones. Vitamin C, niacinamide, and gentle exfoliants help fade discoloration over time.',
      'Dullness / lack of glow':      'Dull skin often needs gentle exfoliation and hydration. Glow-boosting ingredients help restore radiance.',
      'Not sure':                     'A balanced, gentle routine is a great starting point. Focus on the basics: cleanser, moisturizer, and sunscreen.'
    };

    /* ------------------------------------------------------------------
       Product engine
    ------------------------------------------------------------------ */
    function getProducts() {
      var el = root.querySelector('[data-quiz-products]');
      if (!el) return [];
      try { return JSON.parse(el.textContent) || []; }
      catch (e) { console.error('Skin quiz: could not parse product data', e); return []; }
    }

    function normalizeTag(t) { return String(t || '').toLowerCase().trim(); }

    function productHasTag(product, tag) {
      if (!tag) return false;
      var target = normalizeTag(tag);
      return (product.tags || []).some(function (t) { return normalizeTag(t) === target; });
    }

    function pickRandom(list) {
      if (!list || !list.length) return null;
      return list[Math.floor(Math.random() * list.length)];
    }

    /**
     * Pick one product for a routine step using a 4-tier fallback so that
     * gaps in concern tagging never produce an empty card when stock exists.
     *
     * Tier 1 (ideal):   concern + product type + skin type
     * Tier 2:           concern + product type             (drop skin type)
     * Tier 3:           product type + skin type           (drop concern)
     * Tier 4 (last):    product type only                  (drop both)
     *
     * Skin-type preference is applied as a soft filter at each tier: if
     * skin-type-matched products exist they're preferred, otherwise the
     * full tier pool is used. This keeps the best-fit logic without
     * artificially narrowing results.
     */
    function preferBySkinType(candidates, skinType) {
      if (!skinType || !candidates.length) return candidates;
      var matched = candidates.filter(function (p) { return productHasTag(p, skinType); });
      return matched.length ? matched : candidates;
    }

    function pickProductForStep(all, concernTag, productType, skinType) {
      var byType = all.filter(function (p) { return productHasTag(p, productType); });
      if (!byType.length) return null;

      // Tier 1 + 2: products that match concern AND product type.
      var byConcern = concernTag
        ? byType.filter(function (p) { return productHasTag(p, concernTag); })
        : [];

      if (byConcern.length) {
        return pickRandom(preferBySkinType(byConcern, skinType));
      }

      // Tier 3 + 4: no concern match — fall back to any product of this type.
      return pickRandom(preferBySkinType(byType, skinType));
    }

    /**
     * Sunscreen picker. Skin tone is the primary signal (avoids white cast),
     * then concern, then skin type — each applied as a narrowing soft filter.
     * Falls back through the same tiers as pickProductForStep if concern
     * tagging is incomplete.
     */
    function pickSunscreen(all, concernTag, skinType, skinTone) {
      var pool = all.filter(function (p) { return productHasTag(p, 'Sunscreen'); });
      if (!pool.length) return null;

      // Tone is a hard-ish preference: apply first, keep result if non-empty.
      if (skinTone) {
        var toneMatched = pool.filter(function (p) { return productHasTag(p, skinTone); });
        if (toneMatched.length) pool = toneMatched;
      }

      // Narrow by concern if possible, otherwise keep the tone-filtered pool.
      if (concernTag) {
        var concernMatched = pool.filter(function (p) { return productHasTag(p, concernTag); });
        if (concernMatched.length) pool = concernMatched;
        // If no concern match, pool stays as tone-matched (or full) — that's
        // the Tier 3/4 equivalent for sunscreen.
      }

      // Soft skin-type preference within whatever pool we have.
      return pickRandom(preferBySkinType(pool, skinType));
    }

    function buildRoutine(skinType, concernTag, skinTone) {
      var all      = getProducts();
      var profile  = resolveRoutineProfile();
      var optionals = profile.optionalCategories || [];
      var routine  = [];

      profile.categories.forEach(function (category) {
        var product = pickProductForStep(all, concernTag, category, skinType);
        routine.push({ category: category, product: product, empty: !product, optional: optionals.indexOf(category) !== -1 });
      });

      var sunscreen = pickSunscreen(all, concernTag, skinType, skinTone);
      routine.push({ category: 'Sunscreen', product: sunscreen, empty: !sunscreen, optional: false });

      return routine;
    }

    /* ------------------------------------------------------------------
       Klaviyo
    ------------------------------------------------------------------ */
    function klaviyoAvailable() {
      return typeof window.klaviyo !== 'undefined' && typeof window.klaviyo.push === 'function';
    }

    function identifyVisitor(person) {
      if (!klaviyoAvailable() || !person || !person.email) return;
      window.klaviyo.push(['identify', {
        '$email':      person.email,
        '$first_name': person.name || '',
        '$consent':    person.consent ? ['email'] : []
      }]);
    }

    function buildQuizCompletedProperties(skinType, concernTag, skinTone, routine) {
      return {
        AgeRange:         (answers.age      && answers.age.value)     || '',
        MainConcern:      (answers.concern   && answers.concern.value) || '',
        ConcernTag:       concernTag || '',
        SkinType:         skinType   || '',
        SkinTone:         skinTone   || '',
        RoutineStyle:     (answers.routine   && answers.routine.value) || '',
        RoutineItemCount: routine.filter(function (s) { return !s.empty; }).length,
        Routine: routine.map(function (step) {
          return {
            Category:        step.category,
            Optional:        !!step.optional,
            Available:       !step.empty,
            ProductName:     step.product ? step.product.title    : null,
            ProductURL:      step.product ? step.product.url      : null,
            ProductImageURL: step.product ? step.product.image    : null,
            ProductPrice:    step.product ? step.product.price    : null
          };
        })
      };
    }

    function trackQuizCompleted(skinType, concernTag, skinTone, routine) {
      if (!klaviyoAvailable()) return;
      window.klaviyo.push(['track', 'Skin Quiz Completed',
        buildQuizCompletedProperties(skinType, concernTag, skinTone, routine)
      ]);
    }

    /* ------------------------------------------------------------------
       Add to Cart
    ------------------------------------------------------------------ */
    function cartRoute(path) {
      var base = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return base.replace(/\/$/, '') + path;
    }

    function postCartAdd(items) {
      return fetch(cartRoute('/cart/add.js'), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ items: items })
      }).then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw new Error((data && (data.description || data.message)) || 'Could not add to cart.');
          return data;
        });
      });
    }

    function notifyCartUpdated(source) {
      fetch(cartRoute('/cart.js'))
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { itemCount: cart.item_count, source: source || 'skin-quiz' } }
          }));
        })
        .catch(function () {
          document.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: { data: { source: source || 'skin-quiz' } }
          }));
        });
    }

    function setButtonState(btn, labelEl, state, customLabel) {
      if (!btn || !labelEl) return;
      if (!btn.getAttribute('data-default-label')) {
        btn.setAttribute('data-default-label', labelEl.textContent);
      }
      btn.classList.remove('is-loading', 'is-success', 'is-error');

      if (state === 'loading') {
        btn.disabled = true;
        btn.classList.add('is-loading');
        labelEl.textContent = customLabel || 'Adding…';
      } else if (state === 'success') {
        btn.disabled = false;
        btn.classList.add('is-success');
        labelEl.textContent = customLabel || 'Added ✓';
        window.setTimeout(function () {
          btn.classList.remove('is-success');
          labelEl.textContent = btn.getAttribute('data-default-label');
        }, 2200);
      } else if (state === 'error') {
        btn.disabled = false;
        btn.classList.add('is-error');
        labelEl.textContent = customLabel || 'Try again';
        window.setTimeout(function () {
          btn.classList.remove('is-error');
          labelEl.textContent = btn.getAttribute('data-default-label');
        }, 2600);
      } else {
        btn.disabled = false;
        labelEl.textContent = btn.getAttribute('data-default-label');
      }
    }

    function showCartFeedback(message, isError) {
      var el = root.querySelector('[data-quiz-cart-feedback]');
      if (!el) return;
      el.textContent = message;
      el.hidden      = false;
      el.classList.toggle('skin-quiz__cart-feedback--error', !!isError);
      window.setTimeout(function () { el.hidden = true; }, 3500);
    }

    function addSingleProductToCart(product, btn, labelEl) {
      if (!product || !product.variantId) {
        showCartFeedback("Sorry, that item isn’t available right now.", true);
        return;
      }
      setButtonState(btn, labelEl, 'loading');
      postCartAdd([{ id: product.variantId, quantity: 1 }])
        .then(function () {
          setButtonState(btn, labelEl, 'success');
          notifyCartUpdated('skin-quiz-single');
        })
        .catch(function (err) {
          console.error('Skin quiz: add to cart failed', err);
          setButtonState(btn, labelEl, 'error');
          showCartFeedback(err.message || 'Could not add that item to your cart.', true);
        });
    }

    function addAllToCart(routine, btn, labelEl) {
      var items = routine
        .filter(function (s) { return s.product && s.product.variantId; })
        .map(function (s) { return { id: s.product.variantId, quantity: 1 }; });

      if (!items.length) {
        showCartFeedback("We don’t have any available products in this routine yet.", true);
        return;
      }
      setButtonState(btn, labelEl, 'loading', 'Adding all…');
      postCartAdd(items)
        .then(function () {
          var n = items.length;
          setButtonState(btn, labelEl, 'success', 'Added ' + n + ' item' + (n === 1 ? '' : 's') + ' ✓');
          notifyCartUpdated('skin-quiz-add-all');
          showCartFeedback('Added ' + n + ' product' + (n === 1 ? '' : 's') + ' to your cart.', false);
        })
        .catch(function (err) {
          console.error('Skin quiz: add all to cart failed', err);
          setButtonState(btn, labelEl, 'error');
          showCartFeedback(err.message || 'Could not add the routine to your cart.', true);
        });
    }

    /* ------------------------------------------------------------------
       Results rendering
    ------------------------------------------------------------------ */
    function renderResults() {
      var skinType   = resolveSkinType();
      var concernTag = resolveConcernTag();
      var skinTone   = resolveSkinTone();
      var routine    = buildRoutine(skinType, concernTag, skinTone);
      currentRoutine = routine;

      trackQuizCompleted(skinType, concernTag, skinTone, routine);

      var summaryEl = root.querySelector('[data-quiz-results-summary]');
      if (summaryEl) {
        summaryEl.textContent = visitor.name
          ? 'Hi ' + visitor.name + ', here’s your personalised K-Beauty routine.'
          : 'Here’s your personalised K-Beauty routine.';
      }

      var profileEl = root.querySelector('[data-quiz-profile]');
      if (profileEl) renderProfile(profileEl, skinType);

      var routineEl = root.querySelector('[data-quiz-routine]');
      if (routineEl) {
        routineEl.innerHTML = '';
        if (!routine.length) {
          var empty = document.createElement('p');
          empty.className   = 'skin-quiz__empty-state';
          empty.textContent = "We couldn’t find a perfect match yet, but our team would love to help you find the right routine.";
          routineEl.appendChild(empty);
        } else {
          routine.forEach(function (step) { routineEl.appendChild(buildRoutineStep(step)); });
        }
      }

      var addAllBtn = root.querySelector('[data-quiz-add-all]');
      if (addAllBtn) {
        var hasCartable = routine.some(function (s) { return s.product && s.product.variantId; });
        addAllBtn.hidden = !hasCartable;
        addAllBtn.removeAttribute('data-default-label');
        var addAllLabel = addAllBtn.querySelector('[data-quiz-add-all-label]');
        if (addAllLabel) addAllLabel.textContent = 'Add all to cart';

        if (!addAllBtn.getAttribute('data-quiz-bound')) {
          addAllBtn.setAttribute('data-quiz-bound', 'true');
          addAllBtn.addEventListener('click', function () {
            addAllToCart(currentRoutine, addAllBtn, addAllBtn.querySelector('[data-quiz-add-all-label]'));
          });
        }
      }
    }

    function renderProfile(container, skinType) {
      container.innerHTML = '';
      var rows = [];

      if (answers.age && answers.age.value) {
        rows.push({ label: 'Age range',    value: answers.age.value,     blurb: AGE_BLURBS[answers.age.value] || '' });
      }
      if (skinType) {
        rows.push({ label: 'Skin type',    value: skinType,               blurb: SKIN_TYPE_BLURBS[skinType]    || '' });
      }
      if (answers.concern && answers.concern.value) {
        rows.push({ label: 'Main concern', value: answers.concern.value,  blurb: CONCERN_BLURBS[answers.concern.value] || '' });
      }

      rows.forEach(function (row) {
        var item  = document.createElement('div');
        item.className = 'skin-quiz__profile-item';

        var lbl   = document.createElement('p');
        lbl.className   = 'skin-quiz__profile-label';
        lbl.textContent = row.label;

        var val   = document.createElement('p');
        val.className   = 'skin-quiz__profile-value';
        val.textContent = row.value;

        item.appendChild(lbl);
        item.appendChild(val);

        if (row.blurb) {
          var blurb = document.createElement('p');
          blurb.className   = 'skin-quiz__profile-blurb';
          blurb.textContent = row.blurb;
          item.appendChild(blurb);
        }

        container.appendChild(item);
      });
    }

    function buildRoutineStep(step) {
      var wrapper = document.createElement('div');
      wrapper.className = 'skin-quiz__routine-step';
      if (step.optional) wrapper.classList.add('skin-quiz__routine-step--optional');

      var lbl = document.createElement('p');
      lbl.className   = 'skin-quiz__routine-step-label';
      lbl.textContent = step.category;

      if (step.optional) {
        var badge = document.createElement('span');
        badge.className   = 'skin-quiz__routine-step-badge';
        badge.textContent = 'Optional';
        lbl.appendChild(document.createTextNode(' '));
        lbl.appendChild(badge);
      }

      wrapper.appendChild(lbl);
      wrapper.appendChild(step.empty || !step.product ? buildEmptyCard() : buildProductCard(step.product));
      return wrapper;
    }

    function buildProductCard(product) {
      var card = document.createElement('div');
      card.className = 'skin-quiz__product-card';

      var link = document.createElement('a');
      link.className = 'skin-quiz__product-link';
      link.href      = product.url || '#';

      if (product.image) {
        var img = document.createElement('img');
        img.className = 'skin-quiz__product-image';
        img.src       = product.image;
        img.alt       = product.title || '';
        img.loading   = 'lazy';
        link.appendChild(img);
      }

      var info  = document.createElement('div');
      info.className = 'skin-quiz__product-info';

      var title = document.createElement('p');
      title.className   = 'skin-quiz__product-title';
      title.textContent = product.title || '';
      info.appendChild(title);

      if (product.price) {
        var price = document.createElement('p');
        price.className   = 'skin-quiz__product-price';
        price.textContent = product.price;
        info.appendChild(price);
      }

      link.appendChild(info);
      card.appendChild(link);

      if (product.variantId) {
        var actions = document.createElement('div');
        actions.className = 'skin-quiz__product-actions';

        var addBtn   = document.createElement('button');
        addBtn.type  = 'button';
        addBtn.className = 'skin-quiz__btn skin-quiz__btn--add-one';

        var addLabel = document.createElement('span');
        addLabel.textContent = 'Add to cart';
        addBtn.appendChild(addLabel);

        addBtn.addEventListener('click', function () {
          addSingleProductToCart(product, addBtn, addLabel);
        });

        actions.appendChild(addBtn);
        card.appendChild(actions);
      }

      return card;
    }

    function buildEmptyCard() {
      var card = document.createElement('div');
      card.className = 'skin-quiz__product-card skin-quiz__product-card--empty';
      var msg = document.createElement('p');
      msg.className   = 'skin-quiz__product-empty-msg';
      msg.textContent = "We’re stocking this category soon";
      card.appendChild(msg);
      return card;
    }

    // Kick off.
    showStep('landing');
  }

  function initAll() {
    document.querySelectorAll('[data-skin-quiz]').forEach(function (root) {
      initSkinQuiz(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector && e.target.querySelector('[data-skin-quiz]');
    if (root) initSkinQuiz(root);
  });
})();